/* Turning a Blueprint person into an EZ Leave employee, and finding the one that
 * is already there.
 *
 * Pure on purpose. The client reads three lists and writes one row; every
 * DECISION in that — who counts as the same person, whether a manager can be
 * named, what a new record holds — is here, where a test can reach it without a
 * network. Both failure modes are quiet ones: matching the wrong person writes a
 * stranger's start date over somebody's record, and creating a duplicate gives a
 * person two leave balances.
 *
 * WHAT IDENTIFIES SOMEBODY
 *
 * Blueprint is told by Entra, so it is the authority on who exists. The key is
 * Entra's permanent object id, then the sign-in name (UPN, firstname.lastname@).
 * NOT the email: at Hird 173 of 180 people sign in as one address and receive mail
 * as another (initials@), and some have an account and no mailbox at all, so an
 * email match finds nobody for exactly the people it is needed for.
 *
 * The name is the last resort, accepted only when it is the same person's name
 * and nobody else's, and only for a record that carries neither identifier — an
 * older record, from before they were stamped.
 */

import type { IPerson } from '../models';

/** Who a person's manager is, in the two forms SharePoint can resolve. */
export interface ILeaveManager {
  name: string;
  upn: string;
  mail?: string;
}

/** What EZ Leave is told about somebody. */
export interface ILeaveSource {
  upn: string;
  entraId: string;
  name: string;
  /** Primary mailbox, if there is one. Absent for an account with none. */
  mail?: string;
  /** The depot's name as EZ Leave spells it: Hull, Doncaster or London. */
  depot: string;
  department: string;
  start?: string;
  manager?: ILeaveManager | null;
}

/** The columns of an EZ Leave employee that matching reads. */
export interface IEmployeeRow {
  id: string;
  fields: {
    Title?: string;
    Upn?: string;
    EntraId?: string;
    Email?: string;
    Status?: string;
    LeaverDate?: string;
  };
}

export type MatchedBy = 'entra' | 'upn' | 'mail' | 'name';

export type Found = { row: IEmployeeRow; by: MatchedBy } | { ambiguous: string } | null;

const norm = (s: string | undefined): string => (s ?? '').trim().toLowerCase();

/** A date for an EZ Leave date column, at MIDDAY.
 *
 *  EZ Leave's date columns store an instant, not a day, and a bare date is read as
 *  local midnight, which under BST is 23:00 the previous day in UTC: the record then
 *  reads a day EARLY. For a start date that is a service anniversary a day early, and at
 *  a five-year boundary real holiday; for a last working day it is the day the leaver's
 *  pay is worked out to. EZ Leave's own import learned this the hard way and writes
 *  everything at midday; so does this. Noon UTC is the same calendar day in London all
 *  year round. */
export function atMidday(day: string): string {
  return `${day.slice(0, 10)}T12:00:00Z`;
}

/** The one existing row for this person, if there is one.
 *
 *  Strongest identifier first, and each stronger one decides alone: a record
 *  that carries an EntraId belonging to somebody else is somebody else, whatever
 *  its name says. */
export function findEmployee(rows: readonly IEmployeeRow[], p: ILeaveSource): Found {
  const entra = norm(p.entraId);
  if (entra) {
    const hit = rows.filter((r) => norm(r.fields.EntraId) === entra);
    if (hit.length === 1) return { row: hit[0], by: 'entra' };
    if (hit.length > 1) return { ambiguous: `${hit.length} EZ Leave records carry this Entra ID` };
  }

  const upn = norm(p.upn);
  if (upn) {
    const hit = rows.filter((r) => norm(r.fields.Upn) === upn);
    if (hit.length === 1) return { row: hit[0], by: 'upn' };
    if (hit.length > 1) return { ambiguous: `${hit.length} EZ Leave records carry this sign-in name` };
  }

  const mail = norm(p.mail);
  if (mail) {
    const hit = rows.filter((r) => norm(r.fields.Email) === mail);
    if (hit.length === 1) return { row: hit[0], by: 'mail' };
    if (hit.length > 1) return { ambiguous: `${hit.length} EZ Leave records carry this email` };
  }

  /* By name, and only for a record with no identifier of its own. A row stamped
     with a DIFFERENT person's id is not an old record of this one. */
  const name = norm(p.name);
  if (name) {
    const hit = rows.filter(
      (r) => norm(r.fields.Title) === name && !norm(r.fields.EntraId) && !norm(r.fields.Upn),
    );
    if (hit.length === 1) return { row: hit[0], by: 'name' };
    if (hit.length > 1) return { ambiguous: `${hit.length} EZ Leave records are named ${p.name}` };
  }
  return null;
}

/** Names that are two people on the register, settled once rather than guessed.
 *  The same rule Sync-LeaveManagerFromBlueprint.ps1 carries. */
export const MANAGER_OVERRIDES: Readonly<Record<string, { upn?: string; mail: string }>> = {
  'carl cooper': { mail: 'cc@hird.co.uk' },
};

/** A person's manager as SharePoint can resolve them, or why not.
 *
 *  Blueprint stores the manager as a display name, which is all Entra's expand
 *  returns. A name that is exactly one active person on the register resolves;
 *  none, or several, do not — the field is left blank rather than set to a
 *  guess, because the manager is whose queue the leave lands in. */
export function resolveManager(
  people: readonly IPerson[],
  managerName: string,
): { manager: ILeaveManager } | { problem: string } {
  const want = norm(managerName);
  if (!want) return { problem: 'Blueprint has no manager for them' };

  const override = MANAGER_OVERRIDES[want];
  if (override) {
    return { manager: { name: managerName.trim(), upn: override.upn ?? override.mail, mail: override.mail } };
  }

  const hits = people.filter(
    (p) => p.kind === 'User' && p.status !== 'Leaver' && norm(p.name) === want,
  );
  if (hits.length === 0) return { problem: `Manager "${managerName}" is not on the register` };
  if (hits.length > 1) return { problem: `Manager "${managerName}" is on the register more than once` };
  const m = hits[0];
  return { manager: { name: m.name, upn: m.upn, mail: m.email || undefined } };
}

/** Everything EZ Leave is told about somebody, from Blueprint's own record of them.
 *
 *  The depot comes in by name because EZ Leave spells it Hull, Doncaster, London
 *  and Blueprint holds an id. The manager is resolved from the register, since
 *  Blueprint keeps only their display name. */
export function leaveSourceFor(
  person: IPerson,
  people: readonly IPerson[],
  depotName: string,
): ILeaveSource {
  const m = resolveManager(people, person.mgr);
  return {
    upn: person.upn,
    entraId: person.graphId ?? '',
    name: person.name,
    mail: person.email || undefined,
    depot: depotName,
    department: person.dept,
    start: person.start || undefined,
    manager: 'manager' in m ? m.manager : null,
  };
}

/** What a brand-new EZ Leave record holds: who, where, under whom, from when.
 *
 *  What it does NOT hold is the point. No working pattern, entitlement, carry-over
 *  or payroll number — those are HR's, and a blank pattern is not neutral, it
 *  means no working days. So the record is marked as awaiting its details, which
 *  keeps it out of every calculation until they are added.
 *
 *  Email is the person's mailbox, if they have one, and never their sign-in name:
 *  that is not deliverable for the people with no mailbox, and EZ Leave falls back
 *  to the personal email on the record when this is blank. */
export function newRecordFields(
  p: ILeaveSource,
  ctx: { departmentId?: string; managerLookupId?: string; accountLookupId?: string },
): Record<string, unknown> {
  const f: Record<string, unknown> = {
    Title: p.name,
    Upn: p.upn,
    EntraId: p.entraId,
    Status: 'Active',
    AwaitingDetails: true,
  };
  if (p.mail) f.Email = p.mail;
  if (p.depot) f.Location = p.depot;
  if (p.start) f.StartDate = atMidday(p.start);
  if (ctx.departmentId) f.DepartmentLookupId = ctx.departmentId;
  if (ctx.managerLookupId) f.DepartmentManagerLookupId = ctx.managerLookupId;
  if (ctx.accountLookupId) f.UserAccountLookupId = ctx.accountLookupId;
  return f;
}

/** The identifiers to stamp on an older record that was matched without them, so
 *  the next match is by id and not by name. Only what is missing. */
export function stampFor(row: IEmployeeRow, p: ILeaveSource): Record<string, unknown> {
  const f: Record<string, unknown> = {};
  if (!norm(row.fields.Upn) && p.upn) f.Upn = p.upn;
  if (!norm(row.fields.EntraId) && p.entraId) f.EntraId = p.entraId;
  return f;
}
